/* eslint-disable react-hooks/exhaustive-deps */
import "./ScratchCardScreen.css";
import beforeScratch from "../../../assets/images/scratch-card/before-scratch.png";
import afterScratch from "../../../assets/images/scratch-card/after-scratch.png";
import confettiBurst from "../../../assets/images/scratch-card/confetti-burst.png";
import scratchCardBg from "../../../assets/images/score-card/scratch-card-bg.png";
import scratchCardOverlay from "../../../assets/images/score-card/scratch-card-overlay.png";
import iconNext from "../../../assets/images/scratch-card/icon-next.svg";
import { useRef, useEffect, useState, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import ROUTE_PATHS from "utils/routePaths";
import useWSMApi from "services/api/useWSMApi";
import { useQuery } from "@tanstack/react-query";

const SCRATCH_THRESHOLD = 50;

// ── Confetti ──────────────────────────────────────────────────────────────────
interface Particle {
  x: number;
  y: number;
  vx: number;
  vy: number;
  color: string;
  size: number;
  rotation: number;
  rotationSpeed: number;
  opacity: number;
}
const CONFETTI_COLORS = ["#FF6B35", "#9B5DE5", "#C1F700", "#FFFFFF", "#FFD700"];
const createParticles = (count: number, width: number): Particle[] =>
  Array.from({ length: count }, () => ({
    x: Math.random() * width,
    y: -10,
    vx: (Math.random() - 0.5) * 5,
    vy: Math.random() * 3 + 2,
    color: CONFETTI_COLORS[Math.floor(Math.random() * CONFETTI_COLORS.length)],
    size: Math.random() * 8 + 4,
    rotation: Math.random() * 360,
    rotationSpeed: (Math.random() - 0.5) * 6,
    opacity: 1,
  }));

// ── Persona icon map ──────────────────────────────────────────────────────────
const PERSONA_ICON_MAP: Record<string, string> = {
  Visionary: require("../../../assets/gif/dfw-icons/dfw-Visionaries.gif"),
  Visionaries: require("../../../assets/gif/dfw-icons/dfw-Visionaries.gif"),
  "Free Soul": require("../../../assets/gif/dfw-icons/dfw-Free.gif"),
  Optimists: require("../../../assets/gif/dfw-icons/dfw-Optimists.gif"),
  Connectors: require("../../../assets/gif/dfw-icons/dfw-Club.gif"),
  Creators: require("../../../assets/gif/dfw-icons/dfw-Creators.gif"),
  "Rule-breakers": require("../../../assets/gif/dfw-icons/dfw-Rule-breakers.gif"),
  Innovators: require("../../../assets/gif/dfw-icons/dfw-Innovators.gif"),
  Mavericks: require("../../../assets/gif/dfw-icons/dfw-Mavericks.gif"),
  Dreamers: require("../../../assets/gif/dfw-icons/dfw-Dreamers.gif"),
  Builders: require("../../../assets/gif/dfw-icons/dfw-Team.gif"),
  Thinkers: require("../../../assets/gif/dfw-icons/dfw-Thinkers.gif"),
  Explorers: require("../../../assets/gif/dfw-icons/dfw-Explorer.gif"),
  Wellness: require("../../../assets/gif/dfw-icons/dfw-Welness-Area.gif"),
};
const DEFAULT_ICON = require("../../../assets/gif/dfw-icons/dfw-Visionaries.gif");
const getPersonaIcon = (type?: string): string => {
  if (!type) return DEFAULT_ICON;
  const exact = PERSONA_ICON_MAP[type];
  if (exact) return exact;
  const key = Object.keys(PERSONA_ICON_MAP).find((k) =>
    type.toLowerCase().includes(k.toLowerCase()),
  );
  return key ? PERSONA_ICON_MAP[key] : DEFAULT_ICON;
};

// ─────────────────────────────────────────────────────────────────────────────
const ScratchCardScreen = () => {
  const navigate = useNavigate();
  const { fetchUserScoreCardResult } = useWSMApi();

  const canvasRef = useRef<HTMLCanvasElement>(null);
  const confettiCanvasRef = useRef<HTMLCanvasElement>(null);
  const isDrawing = useRef(false);
  const animFrameRef = useRef<number>(0);
  const particlesRef = useRef<Particle[]>([]);

  const [isRevealed, setIsRevealed] = useState(false);
  const [showConfetti, setShowConfetti] = useState(false);

  const { data: scoreCardResult } = useQuery({
    queryKey: ["get-user-score-card-scratch"],
    queryFn: () => fetchUserScoreCardResult(),
    retry: false,
  });

  const personaTitle: string =
    scoreCardResult?.data?.persona?.title?.replace(/^The\s+/i, "").trim() ??
    "Visionary";
  const personaType = scoreCardResult?.data?.persona?.type ?? personaTitle;
  const personaIcon = getPersonaIcon(personaType);

  // ── Fill canvas with the Figma scratch pattern ────────────────────────────
  const initCanvas = useCallback(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const ctx = canvas.getContext("2d");
    if (!ctx) return;

    ctx.globalCompositeOperation = "source-over";
    ctx.clearRect(0, 0, canvas.width, canvas.height);
    ctx.fillStyle = "#9b9b9d";
    ctx.fillRect(0, 0, canvas.width, canvas.height);

    const img = new Image();
    img.onload = () => {
      if (!canvasRef.current || isRevealed) return;
      ctx.globalCompositeOperation = "source-over";
      ctx.drawImage(img, 0, 0, canvas.width, canvas.height);
    };
    img.src = scratchCardOverlay;
  }, [isRevealed]);

  useEffect(() => {
    if (!isRevealed) setTimeout(() => initCanvas(), 80);
    return () => cancelAnimationFrame(animFrameRef.current);
  }, [initCanvas, isRevealed]);

  // ── Confetti ───────────────────────────────────────────────────────────────
  const startConfetti = useCallback(() => {
    const canvas = confettiCanvasRef.current;
    if (!canvas) return;
    const ctx = canvas.getContext("2d");
    if (!ctx) return;
    particlesRef.current = createParticles(150, canvas.width);
    setShowConfetti(true);
    const animate = () => {
      ctx.clearRect(0, 0, canvas.width, canvas.height);
      particlesRef.current = particlesRef.current
        .map((p) => ({
          ...p,
          x: p.x + p.vx,
          y: p.y + p.vy,
          vy: p.vy + 0.06,
          rotation: p.rotation + p.rotationSpeed,
          opacity: p.y > canvas.height * 0.75 ? p.opacity - 0.015 : p.opacity,
        }))
        .filter((p) => p.opacity > 0 && p.y < canvas.height + 20);
      particlesRef.current.forEach((p) => {
        ctx.save();
        ctx.globalAlpha = p.opacity;
        ctx.translate(p.x, p.y);
        ctx.rotate((p.rotation * Math.PI) / 180);
        ctx.fillStyle = p.color;
        ctx.fillRect(-p.size / 2, -p.size / 4, p.size, p.size / 2);
        ctx.restore();
      });
      if (particlesRef.current.length > 0) {
        animFrameRef.current = requestAnimationFrame(animate);
      } else {
        setShowConfetti(false);
      }
    };
    animFrameRef.current = requestAnimationFrame(animate);
  }, []);

  // ── Scratch helpers ────────────────────────────────────────────────────────
  const getPos = (
    e:
      | React.MouseEvent<HTMLCanvasElement>
      | React.TouchEvent<HTMLCanvasElement>,
    canvas: HTMLCanvasElement,
  ) => {
    const rect = canvas.getBoundingClientRect();
    const scaleX = canvas.width / rect.width;
    const scaleY = canvas.height / rect.height;
    if ("touches" in e) {
      const t = e.touches[0];
      return {
        x: (t.clientX - rect.left) * scaleX,
        y: (t.clientY - rect.top) * scaleY,
      };
    }
    return {
      x: (e.clientX - rect.left) * scaleX,
      y: (e.clientY - rect.top) * scaleY,
    };
  };

  const scratch = (
    e:
      | React.MouseEvent<HTMLCanvasElement>
      | React.TouchEvent<HTMLCanvasElement>,
  ) => {
    if (!isDrawing.current || isRevealed) return;
    const canvas = canvasRef.current;
    if (!canvas) return;
    const ctx = canvas.getContext("2d");
    if (!ctx) return;
    const pos = getPos(e, canvas);
    ctx.globalCompositeOperation = "destination-out";
    ctx.beginPath();
    ctx.arc(
      pos.x,
      pos.y,
      Math.max(canvas.width, canvas.height) * 0.08,
      0,
      Math.PI * 2,
    );
    ctx.fill();
    checkScratchPercent(ctx, canvas);
  };

  const checkScratchPercent = (
    ctx: CanvasRenderingContext2D,
    canvas: HTMLCanvasElement,
  ) => {
    const data = ctx.getImageData(0, 0, canvas.width, canvas.height).data;
    let transparent = 0;
    for (let i = 3; i < data.length; i += 4) {
      if (data[i] < 128) transparent++;
    }
    const percent = Math.round((transparent / (data.length / 4)) * 100);
    if (percent >= SCRATCH_THRESHOLD) {
      ctx.clearRect(0, 0, canvas.width, canvas.height);
      setIsRevealed(true);
      startConfetti();
    }
  };

  return (
    <div className="wms-mobile-container">
      {/* Falling confetti */}
      {showConfetti && (
        <canvas
          ref={confettiCanvasRef}
          className="sc-confetti"
          width={430}
          height={900}
        />
      )}

      <div className="sc-root">
        <img
          src={isRevealed ? afterScratch : beforeScratch}
          alt=""
          className="sc-bg"
        />

        {isRevealed && (
          <img src={confettiBurst} alt="" className="sc-confetti-burst" />
        )}

        {!isRevealed && (
          <div className="sc-scratch-card">
            <div className="sc-scratch-header">SCRATCH HERE</div>
            <div className="sc-scratch-body">
              <div className="sc-scratch-reveal">
                <img
                  src={scratchCardBg}
                  alt=""
                  className="sc-scratch-reveal-bg"
                />
                <div className="sc-scratch-reveal-content">
                  <img
                    src={personaIcon}
                    alt={personaType}
                    className="sc-scratch-icon"
                  />
                  <span className="sc-scratch-name">
                    The <span className="sc-orange">{personaTitle}</span>
                  </span>
                </div>
              </div>
              <canvas
                ref={canvasRef}
                className="sc-canvas"
                width={560}
                height={620}
                onMouseDown={(e) => {
                  isDrawing.current = true;
                  scratch(e);
                }}
                onMouseMove={scratch}
                onMouseUp={() => {
                  isDrawing.current = false;
                }}
                onMouseLeave={() => {
                  isDrawing.current = false;
                }}
                onTouchStart={(e) => {
                  e.preventDefault();
                  isDrawing.current = true;
                  scratch(e);
                }}
                onTouchMove={(e) => {
                  e.preventDefault();
                  scratch(e);
                }}
                onTouchEnd={() => {
                  isDrawing.current = false;
                }}
              />
            </div>
          </div>
        )}

        {isRevealed && (
          <div className="sc-revealed-layout">
            <div className="sc-result-card">
              <img src={scratchCardBg} alt="" className="sc-result-card-bg" />
              <div className="sc-result-card-content">
                <span className="sc-you-are">You Are</span>
                <img
                  src={personaIcon}
                  alt={personaType}
                  className="sc-persona-icon"
                />
                <span className="sc-persona-name">
                  The <span className="sc-orange">{personaTitle}</span>
                </span>
              </div>
            </div>

            <button
              type="button"
              className="sc-cta-button"
              onClick={() =>
                navigate(ROUTE_PATHS.WSM_SCORE_CARD_READINESS_SCREEN)
              }
            >
              <span className="sc-cta-text">
                <span className="sc-cta-unlock">UNLOCK</span>{" "}
                <span className="sc-cta-mystery">MYSTERY</span>
              </span>
              <div className="sc-cta-arrow-box">
                <img src={iconNext} alt="" className="sc-cta-arrow" />
              </div>
            </button>
          </div>
        )}
      </div>
    </div>
  );
};

export default ScratchCardScreen;
